import { useState, useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useNavigate, useParams } from "react-router-dom";

import { Button, PageContainer } from "@swot/base-components";

import { CmtsSmartDataGrid, type CmtsGridColumn } from "../data-grid/index.js";

import { CmtsApiErrorPanel } from "../CmtsApiErrorPanel.js";

import { CmtsEnterpriseModal } from "../CmtsEnterpriseModal.js";

import { CmtsCapaForm } from "./CmtsCapaForm.js";

import { capaApi } from "../../services/capaApi.js";

import { complaintDropdownApi } from "../../services/complaintDropdownApi.js";

import { createIdempotencyKey } from "../../services/api.service.js";

import type { CapaCreatePayload } from "../../core/payloads/cmtsTypedPayloads.js";

import type { CapaDto } from "../../../types/capa.types.js";

import type { DropdownOption } from "../../types/api.types.js";

import { invalidateCmtsLifecycleMutation } from "../../core/query/cmtsLifecycleInvalidation.js";

import { useCmtsSelector } from "../../store/hooks.js";

import { selectEffectivePermissions } from "../../store/selectors.js";

export type CmtsCapaPanelProps = Readonly<{
  selectedRecordId?: string | undefined;
}>;

type EditorState = Readonly<{
  mode: "create" | "edit";
  row?: CapaDto | undefined;
}>;

type CapaGridRow = CapaDto & {
  capaActions?: string | undefined;
};

type CapaActionCardProps = {
  icon: string;
  title: string;
  description: string;
  disabled?: boolean | undefined;
  danger?: boolean | undefined;
  onClick: () => void;
};

const queryKeys = {
  list: (complaintId: string) => ["cmts", "capa", complaintId] as const,

  queue: ["cmts", "capa", "queue"] as const,

  masters: ["cmts", "capa", "masters"] as const,
};

function dateOnly(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : "";
}

function displayDate(value: string | null | undefined): string {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value.slice(0, 10);
  }

  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function statusCode(row: CapaDto): string {
  return String(row.statusCode ?? "")
    .trim()
    .toUpperCase();
}

function statusName(row: CapaDto): string {
  return String(row.statusName ?? row.statusCode ?? "OPEN")
    .trim()
    .toUpperCase();
}

function statusIs(row: CapaDto, ...codes: string[]): boolean {
  const current = statusCode(row);

  return codes.map((code) => code.toUpperCase()).includes(current);
}

function isClosed(row: CapaDto): boolean {
  return statusIs(row, "COMPLETED", "CLOSED");
}

function isOverdue(value: string | null | undefined, closed: boolean): boolean {
  if (!value || closed) return false;
  const due = new Date(value).getTime();
  if (Number.isNaN(due)) return false;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return due < now.getTime();
}

function isDueSoon(value: string | null | undefined, closed: boolean): boolean {
  if (!value || closed) return false;
  const due = new Date(value).getTime();
  if (Number.isNaN(due)) return false;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diff = (due - now.getTime()) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= 3;
}

function optionByCode(
  options: readonly DropdownOption[] | undefined,
  code: string,
): DropdownOption | undefined {
  const expected = code.toUpperCase();

  return options?.find(
    (option) =>
      option.code?.toUpperCase() === expected ||
      option.label.toUpperCase() === expected,
  );
}

function CapaActionCard({
  icon,
  title,
  description,
  disabled = false,
  danger = false,
  onClick,
}: CapaActionCardProps) {
  return (
    <button
      type="button"
      className={[
        "cmts-notification-action-option",
        danger ? "cmts-notification-action-option--danger" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled}
      onClick={onClick}
    >
      <span
        className="cmts-notification-action-option__icon"
        aria-hidden="true"
      >
        {icon}
      </span>

      <span className="cmts-notification-action-option__content">
        <strong>{title}</strong>

        <small>{description}</small>
      </span>

      <span
        className="cmts-notification-action-option__arrow"
        aria-hidden="true"
      >
        ›
      </span>
    </button>
  );
}

export function CmtsCapaPanel(_props: CmtsCapaPanelProps) {
  const { complaintId = "" } = useParams<{
    complaintId?: string;
  }>();

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const permissions = useCmtsSelector(selectEffectivePermissions);

  const can = (code: string): boolean => permissions.includes(code);

  const [editor, setEditor] = useState<EditorState | null>(null);

  const [actionsFor, setActionsFor] = useState<CapaGridRow | null>(null);

  const [actionError, setActionError] = useState<unknown>(null);

  const list = useQuery({
    queryKey: complaintId ? queryKeys.list(complaintId) : queryKeys.queue,

    queryFn: ({ signal }) =>
      complaintId ? capaApi.list(complaintId, signal) : capaApi.queue(signal),
  });

  const actionTypes = useQuery({
    queryKey: [...queryKeys.masters, "actionTypes"],

    queryFn: async ({ signal }) => {
      try {
        return await complaintDropdownApi.master("capa-action-type", {}, signal);
      } catch {
        return [];
      }
    },

    enabled: Boolean(complaintId || editor),
    retry: false,
    staleTime: 60_000,
  });

  const statuses = useQuery({
    queryKey: [...queryKeys.masters, "statuses"],

    queryFn: async ({ signal }) => {
      try {
        return await complaintDropdownApi.master("capa-status", {}, signal);
      } catch {
        return [];
      }
    },

    enabled: Boolean(complaintId || editor || actionsFor),
    retry: false,
    staleTime: 60_000,
  });

  const effectivenessStatuses = useQuery({
    queryKey: [...queryKeys.masters, "effectivenessStatuses"],

    queryFn: async ({ signal }) => {
      try {
        return await complaintDropdownApi.effectivenessStatuses({}, signal);
      } catch {
        return [];
      }
    },

    enabled: Boolean(complaintId || editor || actionsFor),
    retry: false,
    staleTime: 60_000,
  });

  const owners = useQuery({
    queryKey: [...queryKeys.masters, "owners", complaintId],

    queryFn: async ({ signal }) => {
      try {
        return await capaApi.permittedOwners(complaintId, signal);
      } catch {
        return [];
      }
    },

    enabled: Boolean(complaintId && (editor || actionsFor)),
    retry: false,
    staleTime: 60_000,
  });

  const refresh = async (): Promise<void> => {
    if (complaintId) {
      await invalidateCmtsLifecycleMutation(queryClient, complaintId, "capa");
    } else {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.queue,
      });
    }
  };

  const create = useMutation({
    mutationFn: (payload: CapaCreatePayload) =>
      capaApi.create(payload.complaintId, payload, createIdempotencyKey()),

    onSuccess: async () => {
      setEditor(null);
      setActionError(null);
      await refresh();
    },

    onError: (error: unknown) => {
      setActionError(error);
    },
  });

  const update = useMutation({
    mutationFn: ({
      row,
      payload,
    }: {
      row: CapaDto;
      payload: CapaCreatePayload;
    }) => {
      const updatePayload = {
        ...payload,
        ...(row.rowVersion
          ? {
            rowVersion: row.rowVersion,
          }
          : {}),
      };

      return capaApi.update(
        row.complaintId,
        row.id,
        updatePayload,
        createIdempotencyKey(),
      );
    },

    onSuccess: async () => {
      setEditor(null);
      setActionError(null);
      await refresh();
    },

    onError: (error: unknown) => {
      setActionError(error);
    },
  });

  const lifecycle = useMutation({
    mutationFn: async ({
      action,
      row,
    }: {
      action: "submit" | "requestVerification" | "complete" | "reopen";

      row: CapaDto;
    }) => {
      const key = createIdempotencyKey();

      if (action === "complete") {
        const closedStatusId =
          optionByCode(statuses.data, "COMPLETED")?.value ??
          optionByCode(statuses.data, "CLOSED")?.value;

        if (!closedStatusId) {
          throw new Error(
            "The completed CAPA status is not available from governed master data.",
          );
        }

        const effectivenessStatusId = row.effectivenessRequired
          ? optionByCode(effectivenessStatuses.data, "EFFECTIVE")?.value
          : undefined;

        const completePayload: {
          closedStatusId: string;
          effectivenessStatusId?: string | undefined;
          rowVersion?: string | undefined;
        } = {
          closedStatusId,
        };

        if (effectivenessStatusId) {
          completePayload.effectivenessStatusId = effectivenessStatusId;
        }

        if (row.rowVersion) {
          completePayload.rowVersion = row.rowVersion;
        }

        return capaApi.complete(row.complaintId, row.id, completePayload, key);
      }

      const lifecyclePayload: {
        rowVersion?: string | undefined;
      } = {};

      if (row.rowVersion) {
        lifecyclePayload.rowVersion = row.rowVersion;
      }

      return capaApi[action](row.complaintId, row.id, lifecyclePayload, key);
    },

    onSuccess: async () => {
      setActionsFor(null);
      setActionError(null);
      await refresh();
    },

    onError: (error: unknown) => {
      setActionError(error);
    },
  });

  const runLifecycle = async (
    action: "submit" | "requestVerification" | "complete" | "reopen",

    row: CapaDto,
  ): Promise<void> => {
    setActionError(null);

    try {
      await lifecycle.mutateAsync({
        action,
        row,
      });
    } catch {
      /*
       * Keep popup open.
       * Error is displayed inside modal.
       */
    }
  };

  const openEditor = (state: EditorState) => {
    setActionsFor(null);
    setActionError(null);
    setEditor(state);
  };

  const columns: readonly CmtsGridColumn<CapaGridRow>[] = [
    {
      id: "capaCode",
      header: "CAPA",
      sortable: true,
      filterable: true,
      minWidth: 155,

      render: (row) => (
        <div className="cmts-notification-title">
          <strong>{row.capaCode || "CAPA"}</strong>

          <span>{row.actionTypeName || "Corrective action"}</span>
        </div>
      ),
    },
    {
      id: "actionTypeName",
      header: "Action Type",
      sortable: true,
      filterable: true,
      minWidth: 155,

      render: (row) => row.actionTypeName || "-",
    },
    {
      id: "actionDescription",
      header: "Action",
      sortable: true,
      filterable: true,
      minWidth: 250,

      render: (row) => row.actionDescription || "-",
    },
    {
      id: "ownerName",
      header: "Owner",
      sortable: true,
      filterable: true,
      minWidth: 160,

      render: (row) => row.ownerName || "Unassigned",
    },
    {
      id: "dueDate",
      header: "Due Date",
      sortable: true,
      minWidth: 155,

      render: (row) => {
        const closed = isClosed(row);
        const overdue = isOverdue(row.dueDate, closed);
        const dueSoon = isDueSoon(row.dueDate, closed);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            <span>{displayDate(row.dueDate)}</span>
            {overdue && (
              <span style={{ fontSize: "0.72rem", color: "#dc2626", fontWeight: 700 }}>
                ⚠️ Overdue
              </span>
            )}
            {dueSoon && !overdue && (
              <span style={{ fontSize: "0.72rem", color: "#d97706", fontWeight: 600 }}>
                ⏳ Due Soon
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: "effectivenessCheckDate",
      header: "Effectiveness Date",
      sortable: true,
      minWidth: 165,

      accessor: (row) => displayDate(row.effectivenessCheckDate),
    },
    {
      id: "statusName",
      header: "Status",
      sortable: true,
      filterable: true,
      minWidth: 125,

      render: (row) => (
        <span
          className={[
            "cmts-status-chip",
            `cmts-capa-status--${statusCode(row).toLowerCase()}`,
          ].join(" ")}
        >
          {statusName(row)}
        </span>
      ),
    },
    {
      id: "capaActions",
      header: "Actions",
      sortable: false,
      filterable: false,
      minWidth: 125,

      render: (row) => (
        <button
          type="button"
          className="cmts-notification-actions-button"
          aria-label="Open CAPA actions"
          onClick={(event) => {
            event.stopPropagation();
            setActionError(null);
            setActionsFor(row);
          }}
        >
          <span
            className="cmts-notification-actions-button__icon"
            aria-hidden="true"
          >
            ⋮
          </span>

          <span>Actions</span>
        </button>
      ),
    },
  ];

  const error = actionError ?? (list.isError ? list.error : null);

  const [activeFilter, setActiveFilter] = useState<"ALL" | "IN_PROGRESS" | "AWAITING_VERIFICATION" | "COMPLETED" | "OVERDUE">("ALL");

  const rawRows = (list.data ?? []) as readonly CapaGridRow[];

  const metrics = useMemo(() => {
    let inProgress = 0;
    let awaitingVerification = 0;
    let completed = 0;
    let overdue = 0;

    for (const r of rawRows) {
      const closed = isClosed(r);
      const code = statusCode(r);
      if (closed) {
        completed++;
      } else {
        if (code.includes("VERIF") || code === "SUBMITTED") {
          awaitingVerification++;
        } else {
          inProgress++;
        }
        if (isOverdue(r.dueDate, false)) {
          overdue++;
        }
      }
    }

    return {
      total: rawRows.length,
      inProgress,
      awaitingVerification,
      completed,
      overdue,
    };
  }, [rawRows]);

  const displayRows = useMemo(() => {
    if (activeFilter === "ALL") return rawRows;
    if (activeFilter === "IN_PROGRESS") return rawRows.filter((r) => !isClosed(r) && !statusCode(r).includes("VERIF") && statusCode(r) !== "SUBMITTED");
    if (activeFilter === "AWAITING_VERIFICATION") return rawRows.filter((r) => !isClosed(r) && (statusCode(r).includes("VERIF") || statusCode(r) === "SUBMITTED"));
    if (activeFilter === "COMPLETED") return rawRows.filter((r) => isClosed(r));
    if (activeFilter === "OVERDUE") return rawRows.filter((r) => isOverdue(r.dueDate, isClosed(r)));
    return rawRows;
  }, [rawRows, activeFilter]);

  const handleExportSummary = () => {
    const data = displayRows.map((r) => ({
      "CAPA Code": r.capaCode || "CAPA",
      "Action Type": r.actionTypeName || "Corrective action",
      "Description": r.actionDescription || "",
      "Owner": r.ownerName || "Unassigned",
      "Due Date": displayDate(r.dueDate),
      "Effectiveness Check Date": displayDate(r.effectivenessCheckDate),
      "Status": statusName(r),
      "Complaint ID": r.complaintId || "",
    }));

    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `CAPA_Actions_Summary_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const canCreate = Boolean(
    can("CAPA.CREATE") &&
    Boolean(complaintId),
  );

  const selectedClosed = actionsFor !== null && isClosed(actionsFor);

  const selectedCanEdit =
    actionsFor !== null && can("CAPA.UPDATE") && !selectedClosed;

  const selectedCanSubmit =
    actionsFor !== null &&
    can("CAPA.SUBMIT") &&
    !statusIs(actionsFor, "SUBMITTED", "IN_PROGRESS", "COMPLETED", "CLOSED");

  const selectedCanRequestVerification =
    actionsFor !== null && can("CAPA.REQUEST_VERIFICATION") && !selectedClosed;

  const selectedCanComplete =
    actionsFor !== null && can("CAPA.COMPLETE") && !selectedClosed;

  const selectedCanReopen =
    actionsFor !== null &&
    can("CAPA.REOPEN") &&
    statusIs(actionsFor, "COMPLETED", "CLOSED", "REJECTED");

  const busy = create.isPending || update.isPending || lifecycle.isPending;

  return (
    <PageContainer
      title="Corrective and Preventive Actions"
      subtitle={
        complaintId
          ? "Complaint-scoped CAPA records linked directly to the complaint lifecycle."
          : "Tenant and persona-scoped CAPA queue."
      }
    >
      {error && !actionsFor && !editor ? (
        <CmtsApiErrorPanel
          error={error}
          title="CAPA operation failed"
          onRetry={() => void list.refetch()}
        />
      ) : null}

      {/* CAPA Owner KPI Quick-Metric Cards */}
      <div
        className="cmts-kpi-card-grid"
        style={{
          marginBottom: "1.25rem",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
          gap: "0.75rem",
        }}
      >
        <button
          type="button"
          className="cmts-kpi-card"
          style={{
            cursor: "pointer",
            textAlign: "left",
            border: activeFilter === "ALL" ? "2px solid #3b82f6" : undefined,
            background: activeFilter === "ALL" ? "rgba(59, 130, 246, 0.08)" : undefined,
          }}
          onClick={() => setActiveFilter("ALL")}
        >
          <span className="cmts-kpi-card__label">Total Actions</span>
          <strong>{metrics.total}</strong>
          <span className="cmts-kpi-card__trend" style={{ fontSize: "0.75rem", color: "#64748b" }}>All CAPAs</span>
        </button>

        <button
          type="button"
          className="cmts-kpi-card"
          style={{
            cursor: "pointer",
            textAlign: "left",
            border: activeFilter === "IN_PROGRESS" ? "2px solid #2563eb" : undefined,
            background: activeFilter === "IN_PROGRESS" ? "rgba(37, 99, 235, 0.08)" : undefined,
          }}
          onClick={() => setActiveFilter("IN_PROGRESS")}
        >
          <span className="cmts-kpi-card__label">In Progress</span>
          <strong style={{ color: "#2563eb" }}>{metrics.inProgress}</strong>
          <span className="cmts-kpi-card__trend" style={{ fontSize: "0.75rem", color: "#64748b" }}>Active implementation</span>
        </button>

        <button
          type="button"
          className="cmts-kpi-card"
          style={{
            cursor: "pointer",
            textAlign: "left",
            border: activeFilter === "AWAITING_VERIFICATION" ? "2px solid #8b5cf6" : undefined,
            background: activeFilter === "AWAITING_VERIFICATION" ? "rgba(139, 92, 246, 0.08)" : undefined,
          }}
          onClick={() => setActiveFilter("AWAITING_VERIFICATION")}
        >
          <span className="cmts-kpi-card__label">Awaiting Verification</span>
          <strong style={{ color: "#8b5cf6" }}>{metrics.awaitingVerification}</strong>
          <span className="cmts-kpi-card__trend" style={{ fontSize: "0.75rem", color: "#64748b" }}>Pending QA check</span>
        </button>

        <button
          type="button"
          className="cmts-kpi-card"
          style={{
            cursor: "pointer",
            textAlign: "left",
            border: activeFilter === "COMPLETED" ? "2px solid #10b981" : undefined,
            background: activeFilter === "COMPLETED" ? "rgba(16, 185, 129, 0.08)" : undefined,
          }}
          onClick={() => setActiveFilter("COMPLETED")}
        >
          <span className="cmts-kpi-card__label">Completed / Closed</span>
          <strong style={{ color: "#10b981" }}>{metrics.completed}</strong>
          <span className="cmts-kpi-card__trend" style={{ fontSize: "0.75rem", color: "#64748b" }}>Verified effective</span>
        </button>

        <button
          type="button"
          className="cmts-kpi-card"
          style={{
            cursor: "pointer",
            textAlign: "left",
            border: activeFilter === "OVERDUE" ? "2px solid #ef4444" : undefined,
            background: activeFilter === "OVERDUE" ? "rgba(239, 68, 68, 0.08)" : undefined,
          }}
          onClick={() => setActiveFilter("OVERDUE")}
        >
          <span className="cmts-kpi-card__label">Overdue</span>
          <strong style={{ color: metrics.overdue > 0 ? "#ef4444" : "#64748b" }}>{metrics.overdue}</strong>
          <span className="cmts-kpi-card__trend" style={{ fontSize: "0.75rem", color: metrics.overdue > 0 ? "#ef4444" : "#64748b" }}>Past target date</span>
        </button>
      </div>

      <div
        className="cmts-page-actions"
        style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center", marginBottom: "1rem" }}
      >
        {complaintId ? (
          <Button
            label="Create CAPA"
            variant="primary"
            disabled={!canCreate || create.isPending}
            onClick={() =>
              openEditor({
                mode: "create",
              })
            }
          />
        ) : (
          <Button
            label="Open Complaints"
            variant="primary"
            onClick={() => navigate("/cmts/complaints")}
          />
        )}

        <Button
          label="Export CAPA Summary"
          variant="secondary"
          disabled={displayRows.length === 0}
          onClick={handleExportSummary}
        />

        {activeFilter !== "ALL" ? (
          <Button
            label={`Clear Filter (${activeFilter})`}
            variant="ghost"
            onClick={() => setActiveFilter("ALL")}
          />
        ) : null}
      </div>

      <CmtsSmartDataGrid<CapaGridRow>
        title="CAPA Records"
        subtitle={
          activeFilter === "ALL"
            ? "Review corrective and preventive actions, owners, due dates and lifecycle status."
            : `Showing filtered ${activeFilter} records (${displayRows.length} of ${rawRows.length}).`
        }
        rows={displayRows}
        columns={columns}
        rowKey="id"
        loading={list.isLoading}
        error={list.error}
        refreshable
        onRefresh={refresh}
        searchable
        sortable
        filterable
        emptyMessage={
          activeFilter === "ALL"
            ? "No CAPA records are available."
            : `No ${activeFilter} CAPA records found.`
        }
      />

      {/* CAPA Actions popup */}
      <CmtsEnterpriseModal
        open={Boolean(actionsFor)}
        title="CAPA actions"
        subtitle={
          actionsFor
            ? `${actionsFor.capaCode || "CAPA"} • ${actionsFor.actionTypeName || "Corrective action"}`
            : ""
        }
        size="md"
        onClose={() => {
          if (!busy) {
            setActionsFor(null);
            setActionError(null);
          }
        }}
      >
        {actionsFor ? (
          <div className="cmts-notification-action-modal">
            <div className="cmts-notification-action-modal__summary">
              <div>
                <span>Current status</span>

                <strong>{statusName(actionsFor)}</strong>
              </div>

              <div>
                <span>Owner</span>

                <strong>{actionsFor.ownerName || "Unassigned"}</strong>
              </div>

              <div>
                <span>Due date</span>

                <strong>{displayDate(actionsFor.dueDate)}</strong>
              </div>
            </div>

            {actionError ? (
              <div className="cmts-notification-action-modal__error">
                <CmtsApiErrorPanel
                  error={actionError}
                  title="CAPA action failed"
                />
              </div>
            ) : null}

            <div className="cmts-notification-action-modal__grid">
              <CapaActionCard
                icon="↗"
                title="Open complaint"
                description="Open the linked complaint CAPA workspace."
                disabled={busy || !actionsFor.complaintId}
                onClick={() => {
                  const row = actionsFor;

                  setActionsFor(null);

                  navigate(
                    `/cmts/complaints/${encodeURIComponent(
                      row.complaintId,
                    )}/capa`,
                  );
                }}
              />

              <CapaActionCard
                icon="✎"
                title="Edit CAPA"
                description="Update action, owner, due date and effectiveness details."
                disabled={busy || !selectedCanEdit}
                onClick={() => {
                  openEditor({
                    mode: "edit",
                    row: actionsFor,
                  });
                }}
              />

              <CapaActionCard
                icon="➤"
                title="Submit CAPA"
                description="Submit this CAPA into the governed workflow."
                disabled={busy || !selectedCanSubmit}
                onClick={() => {
                  const row = actionsFor;

                  void runLifecycle("submit", row);
                }}
              />

              <CapaActionCard
                icon="✓"
                title="Request verification"
                description="Send this CAPA for independent effectiveness verification."
                disabled={busy || !selectedCanRequestVerification}
                onClick={() => {
                  const row = actionsFor;

                  void runLifecycle("requestVerification", row);
                }}
              />

              <CapaActionCard
                icon="★"
                title="Complete CAPA"
                description="Complete the CAPA using governed completion and effectiveness statuses."
                disabled={busy || !selectedCanComplete}
                onClick={() => {
                  const confirmed = globalThis.confirm(
                    "Do you want to complete this CAPA?",
                  );

                  if (!confirmed) {
                    return;
                  }

                  const row = actionsFor;

                  void runLifecycle("complete", row);
                }}
              />

              <CapaActionCard
                icon="↻"
                title="Reopen CAPA"
                description="Reopen a completed, closed or rejected CAPA."
                disabled={busy || !selectedCanReopen}
                onClick={() => {
                  const row = actionsFor;

                  void runLifecycle("reopen", row);
                }}
              />
            </div>
          </div>
        ) : null}
      </CmtsEnterpriseModal>

      {/* Create/Edit CAPA modal */}
      <CmtsEnterpriseModal
        open={Boolean(editor)}
        title={editor?.mode === "edit" ? "Edit CAPA" : "Create CAPA"}
        subtitle={
          editor?.row
            ? `${editor.row.capaCode} • ${editor.row.actionTypeName}`
            : "Create a corrective or preventive action linked to this complaint."
        }
        size="lg"
        onClose={() => {
          if (!busy) {
            setEditor(null);
            setActionError(null);
          }
        }}
      >
        {editor ? (
          <CmtsCapaForm
            complaintId={editor.row?.complaintId ?? complaintId}
            actionTypes={actionTypes.data ?? []}
            statuses={statuses.data ?? []}
            owners={owners.data ?? []}
            mode={editor.mode}
            busy={create.isPending || update.isPending}
            {...(editor.row
              ? {
                initialValues: {
                  complaintId: editor.row.complaintId,

                  actionTypeId: editor.row.actionTypeId,

                  actionDescription: editor.row.actionDescription,

                  ownerUserId: editor.row.ownerUserId,

                  dueDate: dateOnly(editor.row.dueDate),

                  statusId: editor.row.statusId,

                  effectivenessRequired: editor.row.effectivenessRequired,

                  ...(dateOnly(editor.row.effectivenessCheckDate)
                    ? {
                      effectivenessCheckDate: dateOnly(
                        editor.row.effectivenessCheckDate,
                      ),
                    }
                    : {}),
                },
              }
              : {})}
            onCancel={() => setEditor(null)}
            onSubmit={async (payload) => {
              setActionError(null);

              if (editor.row) {
                await update.mutateAsync({
                  row: editor.row,
                  payload,
                });
              } else {
                await create.mutateAsync(payload);
              }
            }}
          />
        ) : null}
      </CmtsEnterpriseModal>
    </PageContainer>
  );
}
